import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import { compareStoolResults } from '../formula/labComparison';
import { buildLatestCombinedRecord as sharedBuildLatestCombinedRecord } from '../utils/labRecordUtils';
import { STATUS_COLORS } from '../utils/healthStatusColors';

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#34A853'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StoolIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  flex: 1;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  text-align: right;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const stoolColorDisplayMap = {
  'brown': 'สีน้ำตาล',
  'darkbrown': 'สีน้ำตาลเข้ม',
  'dark brown': 'สีน้ำตาลเข้ม',
  'yellow': 'สีเหลือง',
  'black': 'สีดำ',
  'red': 'สีแดง',
  'green': 'สีเขียว',
};

const translateStoolValue = (value) => {
  if (!value) return value;
  const map = {
    'brown': 'สีน้ำตาล (ปกติ)',
    'darkbrown': 'สีน้ำตาลเข้ม (ผิดปกติ)',
    'dark brown': 'สีน้ำตาลเข้ม (ผิดปกติ)',
    'yellow': 'สีเหลือง',
    'notfound': 'ไม่พบ (ปกติ)',
    'not found': 'ไม่พบ (ปกติ)',
    'nowbcseen': 'ไม่พบเม็ดเลือดขาว (ปกติ)',
    'no wbc seen': 'ไม่พบเม็ดเลือดขาว (ปกติ)',
    'norbcseen': 'ไม่พบเม็ดเลือดแดง (ปกติ)',
    'no rbc seen': 'ไม่พบเม็ดเลือดแดง (ปกติ)',
    'negative': 'ไม่พบ (ปกติ)',
    'neg': 'ไม่พบ (ปกติ)',
    'positive': 'พบ',
    'pos': 'พบ',
  };
  return map[value.toLowerCase().trim()] || value;
};

const RESULT_LABELS = {
  stoolColor: 'สีของอุจจาระ',
  stoolConsistency: 'ความข้น/ลักษณะอุจจาระ',
  stoolWbc: 'เม็ดเลือดขาว (WBC)',
  stoolRbc: 'เม็ดเลือดแดง (RBC)',
  parasite: 'พยาธิ, ไข่พยาธิ',
  hemoglobinDetectionStool: 'Hemoglobin detection stool',
};

const getClosestEntry = (history, date) => {
  return history.find((item) => {
    if (!item?.date || !date) return false;
    const diff = Math.abs(new Date(item.date) - new Date(date));
    return diff <= 7 * 24 * 60 * 60 * 1000;
  });
};

const parseFlexibleDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  const raw = String(dateValue).trim();
  if (!raw) return null;

  const isoDate = new Date(raw);
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate;
  }

  const ymdMatch = raw.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (ymdMatch) {
    const [, yearText, monthText, dayText] = ymdMatch;
    return new Date(Number(yearText), Number(monthText) - 1, Number(dayText));
  }

  const dmyMatch = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (dmyMatch) {
    const [, dayText, monthText, yearText] = dmyMatch;
    let year = Number(yearText);
    if (yearText.length === 2) {
      year += year >= 50 ? 1900 : 2000;
    }
    return new Date(year, Number(monthText) - 1, Number(dayText));
  }

  return null;
};

const formatThaiDate = (dateValue) => {
  const date = parseFlexibleDate(dateValue);
  if (!date) return String(dateValue ?? '').trim() || '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear() + 543;
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const StoolTestResultsPage = () => {
  const navigate = useNavigate();
  const [stoolResults, setStoolResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAbnormal, setIsAbnormal] = useState(false);

  useEffect(() => {
    const loadStoolResults = async () => {
      try {
        setLoading(true);
        const memberData = await getMemberLabData();
        const labData = memberData?.labData;

        if (!labData) {
          setStoolResults([]);
          return;
        }

        // ใช้ stool_color เป็น anchor - มี 1 entry = 1 visit
        const colorHistory = labData['stool_color'] || [];
        const consistencyHistory = labData['stool_consistency'] || [];
        const wbcHistory = labData['stool_wbc'] || [];
        const rbcHistory = labData['stool_rbc'] || [];
        const parasiteHistory = labData['stool_parasite'] || [];
        const occultBloodHistory = labData['stool_occult_blood'] || [];

        // ถ้าไม่มีข้อมูลขี้เลย ให้แสดงว่าไม่พบ
        if (
          colorHistory.length === 0 &&
          consistencyHistory.length === 0 &&
          wbcHistory.length === 0 &&
          rbcHistory.length === 0 &&
          parasiteHistory.length === 0 &&
          occultBloodHistory.length === 0
        ) {
          setStoolResults([]);
          return;
        }

        // สร้าง visit list โดย merge ด้วย date (ใกล้เคียงกัน ±1 วัน = visit เดียวกัน)
        // ใช้ colorHistory เป็น base ถ้ามี ไม่งั้นใช้ wbc หรือ rbc
        const baseHistory = colorHistory.length > 0 ? colorHistory :
                            consistencyHistory.length > 0 ? consistencyHistory :
                            wbcHistory.length > 0 ? wbcHistory :
                            rbcHistory.length > 0 ? rbcHistory :
                            parasiteHistory.length > 0 ? parasiteHistory : occultBloodHistory;

        const formattedResults = baseHistory.map((baseEntry) => {
          const rawDate = baseEntry.dateadd || baseEntry.date;
          const date = formatThaiDate(rawDate);
          const colorEntry = getClosestEntry(colorHistory, rawDate);
          const consistencyEntry = getClosestEntry(consistencyHistory, rawDate);
          const wbcEntry = getClosestEntry(wbcHistory, rawDate);
          const rbcEntry = getClosestEntry(rbcHistory, rawDate);
          const parasiteEntry = getClosestEntry(parasiteHistory, rawDate);
          const occultBloodEntry = getClosestEntry(occultBloodHistory, rawDate);

          const results = {};
          if (colorEntry) results.stoolColor = colorEntry.value;
          if (consistencyEntry) results.stoolConsistency = consistencyEntry.value;
          if (wbcEntry) results.stoolWbc = wbcEntry.value;
          if (rbcEntry) results.stoolRbc = rbcEntry.value;
          if (parasiteEntry) results.parasite = parasiteEntry.value;
          if (occultBloodEntry) results.hemoglobinDetectionStool = occultBloodEntry.value;

          return {
            date,
            results,
            comparison: compareStoolResults({
              stoolColor: colorEntry?.value,
              stoolConsistency: consistencyEntry?.value,
              stoolWbc: wbcEntry?.value,
              stoolRbc: rbcEntry?.value,
              parasite: parasiteEntry?.value,
              hemoglobinDetectionStool: occultBloodEntry?.value,
            }),
          };
        });

        setStoolResults(formattedResults);

        // เช็คผลล่าสุดจาก visit ล่าสุดจริง ๆ ให้ตรงกับ dashboard
        const latestStoolRecord = sharedBuildLatestCombinedRecord([
          { field: 'stoolColor', items: colorHistory },
          { field: 'stoolConsistency', items: consistencyHistory },
          { field: 'stoolWbc', items: wbcHistory },
          { field: 'stoolRbc', items: rbcHistory },
          { field: 'parasite', items: parasiteHistory },
          { field: 'hemoglobinDetectionStool', items: occultBloodHistory },
        ]);

        // ปิด logic การตัดสินผิดปกติ/ปกติ (แดง/เขียว) — การ์ดสรุปแสดงแค่ไอคอน + ชื่อหมวดเท่านั้น
        // if (latestStoolRecord) {
        //   const latest = compareStoolResults(latestStoolRecord);
        //   const abnormal = latest?.status === 'abnormal';
        //   setIsAbnormal(abnormal);
        // }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadStoolResults();
  }, []);

  return (
    <TestResultsContainer>
      <MainHeader />

      <TopBar>ผลตรวจอุจจาระ</TopBar>

      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <StoolIcon src="./ผลการตรวจอุจาระสี.png" alt="ผลตรวจอุจจาระ" style={{ width: "32px", height: "32px" }} />
          ผลตรวจอุจจาระ (Stool Test)
        </PageTitle>
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : stoolResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลผลตรวจอุจจาระ
          </div>
        ) : (
          <>
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Header ด้านบนแล้ว */}
            {/* ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) ทั้งหมด — เหลือแค่ไอคอน + ชื่อหมวดเท่านั้น */}
            {/* <StatusCard $bgColor={isAbnormal ? "#ff4757" : "#34A853"}> */}
            {/* <StatusCard $bgColor={STATUS_COLORS.gray}>
              <StoolIcon src="./ผลการตรวจอุจาระสี.png" alt="ผลตรวจอุจจาระ" />
              <StatusInfo>
                <StatusTitle style={{ color: 'white' }}>ผลตรวจอุจจาระ</StatusTitle>
                <StatusTitle style={{ color: 'white' }}>
                  {isAbnormal ? 'พบความผิดปกติ' : 'ผลตรวจปกติ'}
                </StatusTitle>
              </StatusInfo>
            </StatusCard> */}

            {stoolResults.map((result, index) => (
              <ResultCard key={index}>
                <CardHeader>
                  <HeaderText>ผลตรวจอุจจาระ</HeaderText>
                </CardHeader>

                <CardContent>
                  <InfoRow>
                    <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                    <InfoValue>{result.date}</InfoValue>
                  </InfoRow>

                  <Divider />

                  <SectionTitle>รายการตรวจ</SectionTitle>

                  {Object.entries(result.results).map(([key, value], idx) => {
                    const displayLabel = RESULT_LABELS[key] || key;
                    const isColorField = key === 'stoolColor';
                    const comparison = result.comparison?.comparisons?.[key];
                    const isAbnormalValue = comparison ? comparison.status !== 'normal' : false;
                    return (
                      <InfoRow key={idx}>
                        <InfoLabel>{displayLabel}</InfoLabel>
                        <InfoValue>{isColorField ? (stoolColorDisplayMap[value?.toLowerCase().trim()] || value) : translateStoolValue(value)}</InfoValue>
                        {isColorField ? (
                          <InfoValue style={{ color: '#999', fontWeight: 600 }}>-</InfoValue>
                        ) : value && (
                          // หมายเหตุ: สีของรายการตรวจต่อแถวนี้ไม่ได้อยู่ในขอบเขตปิด logic สี (ปิดเฉพาะการ์ดสถานะบนสุดเท่านั้น) — คืนค่าสีแดง/เขียวเดิม
                          <InfoValue style={{ color: isAbnormalValue ? '#e53935' : '#34A853', fontWeight: 600 }}>
                            {key === 'hemoglobinDetectionStool'
                              ? (isAbnormalValue ? 'Positive' : 'Negative')
                              : (isAbnormalValue ? 'Found' : 'Not Found')}
                          </InfoValue>
                        )}
                      </InfoRow>
                    );
                  })}
                </CardContent>
              </ResultCard>
            ))}
          </>
        )}
      </Content>
    </TestResultsContainer>
  );
};

export default StoolTestResultsPage;
